/**
 * Team feature copy (Team Access page / TeamMembersTable). Kept separate from
 * `ui-strings.ts` (already past its own LOC cap, split deliberately deferred)
 * and `admin-strings.ts` (admin console copy, owned by a separate
 * workstream) - one file per concern.
 */
export const TEAM_STRINGS = {
  /**
   * Copy for a member row whose `accountStatus` (see `team.api.ts`) is not
   * 'active' - the Super Admin suspended/deactivated/deleted their account
   * from the users console. Keyed by the non-active statuses only; 'active'
   * never renders any of this copy.
   */
  MEMBER_ACCOUNT_STATUS: {
    BADGE_LABEL: {
      suspended: 'Suspended',
      deactivated: 'Deactivated',
      deleted: 'Deleted',
    },
    /** Short, factual reason surfaced on a control disabled by account status. */
    DISABLED_REASON: {
      suspended: 'Account suspended',
      deactivated: 'Account deactivated',
      deleted: 'Account deleted',
    },
  },
  /**
   * Data-room seats on Team Access (v1.2, FR-138, FR-139; Card 11). A service
   * carries a fixed number of seats and none are bought: seat 1 is locked to
   * the purchaser or the granted admin, and only Premium has a seat 2, which
   * the seat 1 holder gives or moves.
   */
  /**
   * The Data Room Access column on Team Access (FR-139, toggles since
   * 2026-10-14). Seat 1 is locked to its holder; with Premium the seat 1
   * holder switches seat 2 on for one member, and off again to free it. An
   * Issue Report's one seat is the buyer's and cannot move (FR-138).
   */
  DATA_ROOM_SEATS: {
    COLUMN: 'Data Room Access',
    TOGGLE_LABEL: 'Data room access for {name}',
    LOCKED: 'Locked',
    /** Tooltip on the locked seat, by how many seats the service has. */
    LOCKED_HINT: {
      1: 'Locked to the buyer. Issue Report access is not transferable.',
      2: 'This seat stays with its holder.',
    },
    NOT_TRANSFERABLE: 'Not transferable',
    FULL_HINT: 'Both seats are in use. Switch the current holder off first.',
    NEEDS_COMPANY_EMAIL: 'Needs a verified company email',
    RELEASE_TITLE: 'Remove data room access?',
    RELEASE_BODY: '{name} loses data room access straight away. You can then give the seat to someone else.',
    RELEASE_CONFIRM: 'Remove access',
    SAVED: 'Data room access given.',
    RELEASED: 'Data room access removed.',
    FAILED: 'The seat could not be changed.',
  },
  /**
   * Investor member invites and the Sector column (MYN-202, FR-174). The
   * sector is an optional pick from the platform sector taxonomy.
   */
  INVITE_SECTOR: {
    LABEL: 'Sector / area of work',
    PLACEHOLDER: 'No sector',
    COLUMN_HEADER: 'Sector',
    /** Rendered in the Sector column when a member or invite has none. */
    NONE: '-',
    /** Inline (field) errors: the dialog is open, so a toast would sit behind it. */
    ERRORS: {
      INVITE_SECTOR_NOT_ALLOWED: 'A sector cannot be set on this kind of invite.',
      SECTOR_NOT_FOUND: 'That sector is no longer available. Choose another one.',
    },
  },
} as const;
